import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Home,
  BarChart3,
  Map,
  Activity,
  FileText,
  Settings,
  Users,
  PanelLeftClose,
  PanelLeftOpen,
  ChevronDown,
  Search,
  Check
} from 'lucide-react';
import { useLayout } from '../../contexts/LayoutContext';
import { useAuth } from '../../contexts/AuthContext';
import { getAccessibleNavItems } from '../../utils/rbac';
import { BRAND } from '../../config/brand';
import { Badge } from '../ui';
import { useLanguageNavigation, SUPPORTED_LANGUAGES } from '../routing/LanguageWrapper';
import './Sidebar.css';

const allNavItems = [
  { id: 'dashboard', label: 'nav.dashboard', icon: Home, path: '/dashboard' },
  { id: 'analytics', label: 'nav.analytics', icon: BarChart3, path: '/analytics', badge: 3 },
  { id: 'map', label: 'nav.map', icon: Map, path: '/map' },
  { id: 'stations', label: 'nav.stations', icon: Activity, path: '/stations' },
  { id: 'reports', label: 'nav.reports', icon: FileText, path: '/reports' },
  { id: 'settings', label: 'nav.settings', icon: Settings, path: '/settings' },
  { id: 'users', label: 'nav.users', icon: Users, path: '/users' }
];

export function Sidebar() {
  const { t } = useTranslation();
  const location = useLocation();
  const { lang } = useParams();
  const { getLocalizedPath, currentLang } = useLanguageNavigation();
  const { sidebarCollapsed, toggleSidebar, selectedFarm, farms, selectFarm, mobileMenuOpen, setMobileMenuOpen } = useLayout();
  const { user: currentUser } = useAuth();
  const [farmDropdownOpen, setFarmDropdownOpen] = useState(false);
  const [farmSearchQuery, setFarmSearchQuery] = useState('');
  const farmButtonRef = useRef(null);
  const farmDropdownRef = useRef(null);
  const [farmDropdownStyle, setFarmDropdownStyle] = useState(null);

  // Filter nav items based on user role
  const navItems = getAccessibleNavItems(allNavItems, currentUser.role);

  // Filter farms based on search
  const filteredFarms = farms.filter(farm =>
    farm.name.toLowerCase().includes(farmSearchQuery.toLowerCase()) ||
    farm.location.toLowerCase().includes(farmSearchQuery.toLowerCase())
  );

  // Check if path is active (accounting for language prefix)
  const isActive = (path) => {
    // Get current path without language prefix
    const pathSegments = location.pathname.split('/').filter(Boolean);
    let currentPath = location.pathname;
    
    if (pathSegments.length > 0 && SUPPORTED_LANGUAGES.includes(pathSegments[0])) {
      currentPath = '/' + pathSegments.slice(1).join('/');
    }
    
    return currentPath === path || (path === '/dashboard' && currentPath === '/');
  };

  const updateFarmDropdownPosition = () => {
    const btn = farmButtonRef.current;
    const dd = farmDropdownRef.current;
    if (!btn || !dd) return;

    const btnRect = btn.getBoundingClientRect();
    const ddRect = dd.getBoundingClientRect();
    const gap = 8;
    const MIN_DROPDOWN_WIDTH = 360;
    const MAX_DROPDOWN_WIDTH = 460;

    // Prefer opening above the button (snug), fallback below if not enough space.
    const canOpenAbove = btnRect.top - ddRect.height - gap > 8;
    const top = canOpenAbove
      ? Math.round(btnRect.top - ddRect.height - gap)
      : Math.round(btnRect.bottom + gap);

    // Align to the button's left edge and use a larger min width for better readability.
    const left = Math.round(btnRect.left);
    const width = Math.min(
      MAX_DROPDOWN_WIDTH,
      Math.max(MIN_DROPDOWN_WIDTH, Math.round(btnRect.width))
    );

    // Keep within viewport horizontally
    const maxLeft = Math.max(8, window.innerWidth - width - 8);
    const clampedLeft = Math.min(Math.max(8, left), maxLeft);

    setFarmDropdownStyle({
      top,
      left: clampedLeft,
      width,
    });
  };

  // Recompute dropdown position when opening and on viewport changes.
  useLayoutEffect(() => {
    if (!farmDropdownOpen || sidebarCollapsed) {
      setFarmDropdownStyle(null);
      return;
    }
    // First paint, then measure (dropdown height depends on content)
    requestAnimationFrame(() => {
      updateFarmDropdownPosition();
    });
  }, [farmDropdownOpen, sidebarCollapsed, farms.length]);

  useEffect(() => {
    if (!farmDropdownOpen || sidebarCollapsed) return;
    const onResize = () => updateFarmDropdownPosition();
    // Capture scroll events from any scroll container
    const onScroll = () => updateFarmDropdownPosition();
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [farmDropdownOpen, sidebarCollapsed]);

  return (
    <>
      {/* Mobile Backdrop */}
      {mobileMenuOpen && (
        <div
          className="sidebar-backdrop"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      <aside className={`sidebar ${sidebarCollapsed ? 'sidebar--collapsed' : ''} ${mobileMenuOpen ? 'sidebar--mobile-open' : ''}`}>
        {/* Logo Section */}
        <div className="sidebar__logo">
          <img src={BRAND.iconPath} alt={`${BRAND.name} logo`} className="sidebar__logo-icon-img" />
          <span className="sidebar__logo-wordmark">{BRAND.wordmark}</span>
        </div>

        {/* Navigation Items */}
        <nav className="sidebar__nav">
          {navItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            // Use language-prefixed path
            const localizedPath = getLocalizedPath(item.path);

            return (
              <Link
                key={item.id}
                to={localizedPath}
                className={`sidebar__nav-item ${active ? 'sidebar__nav-item--active' : ''}`}
                data-testid={`nav-${item.id}`}
                title={sidebarCollapsed ? t(item.label) : undefined}
                onClick={() => setMobileMenuOpen(false)}
              >
                <Icon size={20} className="sidebar__nav-icon" />
                {!sidebarCollapsed && (
                  <>
                    <span className="sidebar__nav-label">{t(item.label)}</span>
                    {item.badge && (
                      <Badge
                        variant="danger"
                        size="sm"
                        className="sidebar__nav-badge"
                      >
                        {item.badge}
                      </Badge>
                    )}
                  </>
                )}
                {sidebarCollapsed && item.badge && (
                  <span className="sidebar__nav-badge-dot"></span>
                )}
              </Link>
            );
          })}
        </nav>

        {/* Farm Selector - only shown when expanded */}
        {!sidebarCollapsed && (
          <div className="sidebar__farm-selector">
            <div className="sidebar__farm-label">
              {t('farm.selectedFarm')}
            </div>
            <button
              className="sidebar__farm-button"
              onClick={() => setFarmDropdownOpen(!farmDropdownOpen)}
              data-testid="farm-selector"
              ref={farmButtonRef}
            >
              <div className="sidebar__farm-info">
                <div className="sidebar__farm-name">{selectedFarm.name}</div>
                <div className="sidebar__farm-count">
                  {selectedFarm.activeStations} {t('farm.activeStations')}
                </div>
              </div>
              <ChevronDown
                size={16}
                className={`sidebar__farm-chevron ${farmDropdownOpen ? 'sidebar__farm-chevron--open' : ''}`}
              />
            </button>
          </div>
        )}

        {/* Collapse Toggle Button */}
        <button
          className="sidebar__toggle"
          onClick={toggleSidebar}
          aria-label={sidebarCollapsed ? t('nav.expand') : t('nav.collapse')}
          data-testid="sidebar-toggle"
        >
          {sidebarCollapsed ? (
            <PanelLeftOpen size={20} />
          ) : (
            <>
              <PanelLeftClose size={20} />
              <span>{t('nav.collapse')}</span>
            </>
          )}
        </button>
      </aside>

      {/* Farm Selector Dropdown */}
      {farmDropdownOpen && !sidebarCollapsed && (
        <>
          <div
            className="farm-dropdown__backdrop"
            onClick={() => setFarmDropdownOpen(false)}
          />
          <div
            className="farm-dropdown"
            data-testid="farm-dropdown"
            ref={farmDropdownRef}
            style={farmDropdownStyle || undefined}
          >
            {/* Search Input */}
            <div className="farm-dropdown__search">
              <Search size={16} className="farm-dropdown__search-icon" />
              <input
                type="text"
                placeholder={t('farm.searchFarms')}
                value={farmSearchQuery}
                onChange={(e) => setFarmSearchQuery(e.target.value)}
                className="farm-dropdown__search-input"
                autoFocus
              />
            </div>

            {/* Farm List */}
            <div className="farm-dropdown__list">
              {filteredFarms.map((farm) => (
                <button
                  key={farm.id}
                  className={`farm-dropdown__item ${farm.id === selectedFarm.id ? 'farm-dropdown__item--active' : ''
                    }`}
                  onClick={() => {
                    selectFarm(farm);
                    setFarmDropdownOpen(false);
                    setFarmSearchQuery('');
                  }}
                  data-testid={`farm-item-${farm.id}`}
                >
                  <div className="farm-dropdown__item-info">
                    <div className="farm-dropdown__item-name">{farm.name}</div>
                    <div className="farm-dropdown__item-location">{farm.location}</div>
                  </div>
                  <div className="farm-dropdown__item-right">
                    {farm.id === selectedFarm.id && (
                      <Check size={18} className="farm-dropdown__item-check" />
                    )}
                    <Badge variant="info" size="sm">
                      {farm.activeStations}
                    </Badge>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </>
  );
}
